import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const SOPResources = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <section className="section">
      <h2 className="section-title">SOP Management</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        Advanced management operations for SOPs. Update steps, move steps within the SOP, answer
        clarification questions, apply natural language instructions, and publish SOPs. These endpoints
        allow you to refine and finalize your SOPs.
      </p>

      <EndpointCard
        method="GET"
        path="/sops/{sop_uuid}/download"
        title="Download SOP (JSON)"
        description="Downloads the SOP as a JSON file attachment. By default, the embedded content block is omitted to avoid duplication; pass include_content=true to include it."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP to download' }
          },
          query: {
            include_content: { required: false, description: 'Set to true to include the embedded content block (may duplicate scope/steps). Default: false' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Examples:</h4>
            <CodeBlock code={`# Minimal (default, no embedded content)
curl -L -X GET "${BASE_URL}/sops/{sop_uuid}/download" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -o sop_{sop_uuid}.json

# Full (include embedded content block)
curl -L -X GET "${BASE_URL}/sops/{sop_uuid}/download?include_content=true" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -o sop_{sop_uuid}_full.json`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <p className="text-xs text-gray-400 mb-2">Headers:</p>
            <CodeBlock code={`Content-Type: application/json
Content-Disposition: attachment; filename="sop_{sop_uuid}.json"`} />
            <p className="text-xs text-gray-400 mt-2">Body: SOP JSON object (minimal by default)</p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Error Responses:</h4>
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-400">
              <li><code className="bg-red-900/50 px-1 py-0.5 rounded">401</code> - API Key required</li>
              <li><code className="bg-red-900/50 px-1 py-0.5 rounded">403</code> - Not authorized to access this SOP</li>
              <li><code className="bg-red-900/50 px-1 py-0.5 rounded">404</code> - SOP not found</li>
              <li><code className="bg-red-900/50 px-1 py-0.5 rounded">422</code> - Validation error</li>
            </ul>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/sops/{sop_uuid}/clarifications"
        title="Get Clarifications"
        description="Retrieves all clarification questions for an SOP. These are automatically generated when an SOP is created and help identify areas for improvement. Can optionally filter by status (PENDING, ANSWERED, RESOLVED, REJECTED)."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP' }
          },
          query: {
            status: { required: false, description: 'Filter by clarification status (PENDING, ANSWERED, RESOLVED, REJECTED)' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/sops/{sop_uuid}/clarifications?status=PENDING" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`[
  {
    "uuid": "{clarification_uuid}",
    "sop_uuid": "{sop_uuid}",
    "step_uuid": "{step_uuid}",
    "clarification_type": "GAP",
    "question_text": "What should happen if the customer is still unsatisfied after escalation?",
    "answer_text": null,
    "status": "PENDING",
    "clarification_metadata": null
  }
]`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/clarifications/{clarification_uuid}/answer"
        title="Answer Clarification"
        description="Answers a clarification question and triggers automatic proposal generation. The proposal is created asynchronously and can be retrieved via the proposals endpoint after a few seconds."
        tags={['Authentication: Required', 'Async: Yes']}
        parameters={{
          path: {
            clarification_uuid: { required: true, description: 'The unique identifier of the clarification' }
          }
        }}
        requestBody={{
          answer_text: "If the customer is still unsatisfied after escalation, the case should be forwarded to senior management with a detailed summary."
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "answer_text": "If the customer is still unsatisfied after escalation, the case should be forwarded to senior management with a detailed summary."
}`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/clarifications/{clarification_uuid}/answer" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "answer_text": "If the customer is still unsatisfied after escalation, the case should be forwarded to senior management."
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (202 Accepted):</h4>
            <CodeBlock code={`{
  "clarification": {
    "uuid": "{clarification_uuid}",
    "sop_uuid": "{sop_uuid}",
    "step_uuid": "{step_uuid}",
    "clarification_type": "GAP",
    "question_text": "What should happen if the customer is still unsatisfied after escalation?",
    "answer_text": "If the customer is still unsatisfied after escalation, the case should be forwarded to senior management.",
    "status": "ANSWERED",
    "clarification_metadata": null
  },
  "message": "Answer recorded. Proposal generation in progress. Check proposals endpoint for status."
}`} />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>Note:</strong> Proposal generation happens asynchronously. Wait a few seconds after answering,
              then check the proposals endpoint to see the generated change proposal.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/sops/{sop_uuid}/steps"
        title="Create SOP Step"
        description="Creates a new step in an SOP. The step will be added at the specified order_index, or at the end if not provided."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP' }
          }
        }}
        requestBody={{
          step_id: "step_5",
          action_text: "Review and approve the request",
          order_index: 4,
          step_metadata: {
            title: "Final Review",
            description: "Detailed review process"
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "step_id": "step_5",
  "action_text": "Review and approve the request",
  "order_index": 4,
  "step_metadata": {
    "title": "Final Review",
    "description": "Detailed review process",
    "inputs": ["Request form", "Supporting documents"],
    "outputs": ["Approval decision"],
    "business_logic": ["Must verify all required fields"],
    "prerequisites": ["Previous steps completed"],
    "detailed_sub_steps": [
      {
        "title": "Check completeness",
        "description": "Verify all fields are filled",
        "order": 1
      }
    ]
  }
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>step_id</strong> (required): Unique identifier for the step (e.g., step_1, step_2)<br/>
              <strong>action_text</strong> (required): The main action description<br/>
              <strong>order_index</strong> (required): Position in the step sequence (0-based)<br/>
              <strong>step_metadata</strong> (optional): Additional structured metadata for the step
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/sops/{sop_uuid}/steps" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "step_id": "step_5",
    "action_text": "Review and approve the request",
    "order_index": 4,
    "step_metadata": {
      "title": "Final Review"
    }
  }'`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/sops/{sop_uuid}/steps"
        title="List SOP Steps"
        description="Retrieves all steps for an SOP in order."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/sops/{sop_uuid}/steps" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response:</h4>
            <CodeBlock code={`[
  {
    "uuid": "123e4567-e89b-12d3-a456-426614174000",
    "sop_uuid": "123e4567-e89b-12d3-a456-426614174001",
    "step_id": "step_1",
    "action_text": "Receive and validate the request",
    "order_index": 0,
    "step_metadata": {
      "title": "Request Validation",
      "description": "Initial validation of the request"
    }
  }
]`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/sops/{sop_uuid}/steps/{step_id}"
        title="Get SOP Step"
        description="Retrieves a single step by its step_id."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP' },
            step_id: { required: true, description: 'The step_id of the step to retrieve (e.g., step_1)' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/sops/{sop_uuid}/steps/step_1" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response:</h4>
            <CodeBlock code={`{
  "uuid": "123e4567-e89b-12d3-a456-426614174000",
  "sop_uuid": "123e4567-e89b-12d3-a456-426614174001",
  "step_id": "step_1",
  "action_text": "Receive and validate the request",
  "order_index": 0,
  "step_metadata": {
    "title": "Request Validation",
    "description": "Initial validation of the request",
    "inputs": ["Request form"],
    "outputs": ["Validated request"],
    "business_logic": ["All required fields must be present"]
  }
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="PATCH"
        path="/sops/{sop_uuid}/steps/{step_id}"
        title="Update SOP Step"
        description="Directly updates a step in an SOP. This is a synchronous operation that immediately modifies the step. For AI-assisted updates, use the proposals workflow instead."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP' },
            step_id: { required: true, description: 'The step_id of the step to update (e.g., step_1)' }
          }
        }}
        requestBody={{
          action_text: "Updated action text",
          step_metadata: {
            title: "Updated Step Title"
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "action_text": "Updated action text",
  "step_metadata": {
    "title": "Updated Step Title",
    "description": "Updated description"
  }
}`} />
            <p className="text-xs text-gray-400 mt-2">
              All fields are optional. Only include fields you want to update.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X PATCH "${BASE_URL}/sops/{sop_uuid}/steps/step_1" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "action_text": "Updated action text",
    "step_metadata": {
      "title": "Updated Step Title"
    }
  }'`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="DELETE"
        path="/sops/{sop_uuid}/steps/{step_id}"
        title="Delete SOP Step"
        description="Deletes a step from an SOP. Remaining steps will be automatically reordered to maintain sequential order_index values."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP' },
            step_id: { required: true, description: 'The step_id of the step to delete (e.g., step_1)' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X DELETE "${BASE_URL}/sops/{sop_uuid}/steps/step_1" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
            <p className="text-xs text-gray-400 mt-2">
              Returns 204 No Content on success. The step is permanently deleted and remaining steps are reordered.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/sops/{sop_uuid}/steps/{step_id}:move"
        title="Move SOP Step"
        description="Moves a step to a new position within the SOP. The order_index will be updated and other steps will be reordered accordingly."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP' },
            step_id: { required: true, description: 'The step_id of the step to move (e.g., step_1)' }
          }
        }}
        requestBody={{
          new_index: 0
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "new_index": 0
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>new_index</strong> (required): The new position index (0-based) for the step
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/sops/{sop_uuid}/steps/step_1:move" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "new_index": 0
  }'`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/sops/{sop_uuid}:publish"
        title="Publish SOP"
        description="Publishes an SOP, changing its status from PROPOSED to PUBLISHED. Once published, an SOP is considered finalized and ready for use."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP to publish' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/sops/{sop_uuid}:publish" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{sop_uuid}",
  "project_uuid": "{project_uuid}",
  "title": "Customer Support Escalation SOP",
  "status": "PUBLISHED",
  "version": 1,
  "scope": {
    "goal": "Provide clear steps for handling customer support escalations.",
    "summary": "This SOP outlines the process for escalating customer issues."
  },
  "content": {
    "introduction": "...",
    "conclusion": "..."
  },
  "steps": [...]
}`} />
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default SOPResources;
